import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories, getCategory } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = getCategory(slug);
  const list = getArticlesByCategory(slug);

  if (!category) {
    return (
      <SiteLayout>
        <Seo
          title="栏目未找到 - 爆料在线"
          description="你访问的栏目不存在，可以返回首页浏览独家猛料、内幕调查与微八卦内容。"
          keywords="爆料在线,栏目,独家猛料"
          canonical={`https://mtx360.cn/category/${slug}`}
        />
        <div className="mx-auto w-full max-w-shell px-4 py-20 text-center md:px-6">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-emotion-sad-line text-2xl" aria-hidden="true" />
          </div>
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">没有找到这个栏目</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已经调整或合并，你可以从下面的栏目继续浏览。</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {categories.map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 px-4 py-1.5 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              >
                {item.name}
              </Link>
            ))}
          </div>
          <Link
            to="/"
            className="mt-8 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        name: `${category.name} - 爆料在线`,
        description: category.desc,
        url: `https://mtx360.cn/category/${category.slug}`,
        mainEntity: {
          "@type": "ItemList",
          itemListElement: list.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            url: `https://mtx360.cn/article/${item.id}`,
            name: item.title,
          })),
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://mtx360.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: category.name,
            item: `https://mtx360.cn/category/${category.slug}`,
          },
        ],
      },
    ],
  };

  return (
    <SiteLayout>
      <Seo
        title={`${category.name} - 爆料在线独家${category.name}合集`}
        description={`${category.name}栏目：${category.desc} 爆料在线持续更新共 ${category.total} 篇内容。`}
        keywords={`${category.name},爆料在线,独家猛料,内幕消息,微八卦`}
        canonical={`https://mtx360.cn/category/${category.slug}`}
        type="website"
        jsonLd={jsonLd}
      />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-shell px-4 py-10 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: category.name }]} />
          <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-primary-600">
            <span className="h-px w-6 bg-primary-500" />
            {category.tagline}
          </p>
          <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            {category.name}
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">{category.desc}</p>
          <p className="mt-4 text-xs text-secondary-600">共 {category.total} 篇在库 · 本页展示最新 {list.length} 篇</p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-shell px-4 py-10 md:px-6">
        <div className="mb-8 flex flex-wrap gap-2">
          {categories.map((item) => (
            <Link
              key={item.slug}
              to={`/category/${item.slug}`}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                item.slug === category.slug
                  ? "bg-foreground-950 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {item.name}
            </Link>
          ))}
        </div>

        {list.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        ) : (
          <p className="rounded-lg border border-dashed border-background-300 bg-background-50 px-5 py-12 text-center text-sm text-foreground-600">
            该栏目暂无内容，我们会尽快补充。
          </p>
        )}

        <nav aria-label="分页" className="mt-10 flex items-center justify-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-secondary-500">
            <i className="ri-arrow-left-s-line" aria-hidden="true" />
          </span>
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-sm font-semibold text-background-50">
            1
          </span>
          <span className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300">
            2
          </span>
          <span className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300">
            3
          </span>
          <span className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 hover:border-primary-300">
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
          </span>
        </nav>
      </div>
    </SiteLayout>
  );
}